interface SearchOptions {
  fuzzyThreshold?: number;
}

export type SearchableFields<T> = (keyof T | ((item: T)=>unknown))[];

/**
 * Provides a reactive search utility that allows fuzzy matching and prioritizes fields 
 * based on their order in the `searchableFields` array. Results are sorted by the best match score, with lower scores indicating closer matches.
 * 
 * @template T - The type of data being searched.
 * @param initialData - A reactive source of the data array to search.
 * @param searchableFields - An array of keys or accessor functions to specify which properties of the data should be searchable. Fields earlier in the list have higher priority in the search results.
 * @param options - An optional object for adjusting the fuzzy threshold.
 * @param query - A reactive reference to the search query string.
 * @returns An object containing the `query` reactive reference and `results`, a computed ref of the search results.
*/
export function useSearch<T>(initialData: MaybeRefOrGetter<T[] | ReadonlyArray<T>>, searchableFields: SearchableFields<T>, options: SearchOptions = { }, query: Ref<string> = ref('')) {
  const {
    fuzzyThreshold = 1
  } = options;

  const isNumeric = computed(() => !isNaN(Number(query.value)));

  const results = computed(() => {
    const data = toValue(initialData);

    if (isStringUndefinedNullOrEmpty(query.value)) {
      return data;
    }

    return data
      .map(item => ({
        item,
        score: searchObject(item, searchableFields, query.value)
      }))
      .filter(({ score }) => score !== null)
      .sort((a, b) => {
        if (a.score === null && b.score === null) return 0;

        if (a.score === null) return 1;

        if (b.score === null) return -1;

        return a.score - b.score;
      })
      .map(({ item }) => item);
  });

  function searchObject<T>(obj: T, searchProps: SearchableFields<T>, searchTerm: string): number | null {
    let bestScore: number | null = null;

    for (let i = 0; i < searchProps.length; i++) {
      const prop = searchProps[i];
      const value = typeof prop === 'function' ? prop(obj) : obj[prop];
      const score = matchScore(value, searchTerm);

      if (score !== null) {
        const weightedScore = score + i * fuzzyThreshold;
        bestScore = bestScore === null ? weightedScore : Math.min(bestScore, weightedScore);
      }
    }

    return bestScore;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function matchScore(value: any, searchTerm: string): number | null {
    const normalizedValue = String(value).toLowerCase();
    const normalizedSearchTerm = searchTerm.toLowerCase();

    if (isNumeric.value) {
      return normalizedValue.includes(searchTerm) ? 0 : null;
    }

    if (normalizedValue.includes(normalizedSearchTerm)) {
      return 0; 
    }

    return fuzzySearch(normalizedValue, normalizedSearchTerm) 
      ? levenshteinDistance(normalizedValue, normalizedSearchTerm)
      : null;
  }

  function fuzzySearch(haystack: string, needle: string): boolean {
    return haystack.includes(needle) 
      ? true 
      : levenshteinDistance(haystack, needle) <= fuzzyThreshold;
  }

  function levenshteinDistance(a: string, b: string): number {
    const dp: number[][] = [];

    for (let i = 0; i <= a.length; i++) {
      dp[i] = [];

      for (let j = 0; j <= b.length; j++) {
        if (i === 0) {
          dp[i][j] = j;
        } else if (j === 0) {
          dp[i][j] = i;
        } else {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          dp[i][j] = Math.min(
            dp[i - 1][j - 1] + cost,
            dp[i][j - 1] + 1,
            dp[i - 1][j] + 1
          );
        }
      }
    }

    return dp[a.length][b.length];
  }

  return {
    query,
    results
  };
}
